
import throttle from 'lodash/throttle'
import EoSpinner from '../EoSpinner/EoSpinner'
import EoTransitionAccordion from '../EoTransitionAccordion/EoTransitionAccordion'

export default {
  name: 'EoButton',
  components: { EoSpinner, EoTransitionAccordion },
  props: {
    /**
     * Invisible label for accessibility
     */
    label: {
      type: String,
      required: true,
    },
    /**
     * Hyperlink to url, page or anchor tag
     */
    to: {
      type: String,
      default: null,
    },
    /**
     * Show this tooltip on hover or focus
     */
    tooltip: {
      type: String,
      default: null,
    },
    /**
     * Position of the tooltip: (bottom) (left) (right) (top)
     */
    tooltipPosition: {
      type: String,
      default: 'bottom',
    },
    /**
     * Prefetches the page behind the link
     */
    prefetch: {
      type: Boolean,
      default: true,
    },
    /**
     * Target for the link: (_self) (_blank)
     */
    target: {
      type: String,
      default: '_self',
    },
    /**
     * Type (for use in forms): (button) (reset) (submit)
     */
    type: {
      type: String,
      default: null,
    },
    /**
     * Prevent user interaction with the button
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: horizontally center the button
     */
    centered: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: make button as wide as possible
     */
    wide: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: round version of the button
     */
    round: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: flat version of the button
     */
    flat: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: outlined version of the button
     */
    outlined: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: size of the button: (xs) (sm) (md) (lg) (xl)
     */
    size: {
      type: String,
      validator: prop => Object.values(['xs', 'sm', 'md', 'lg', 'xl']).includes(prop),
      default: 'lg',
    },
    /**
     * Style: show loading indicator inside the button
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: use the button as an invisible skip link
     */
    skipLink: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: color of the button
     */
    color: {
      type: String,
      default: null,
    },
    /**
     * Style: color of the text (if empty, automatically based on color)
     */
    textColor: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      isFocus: false,
      isHover: false,
      isActive: false,
      hasTouchMoved: false,
    }
  },
  computed: {
    classes () {
      return {
        'eo-button': true,
        'is-wide': this.isWide,
        'is-centered': this.isCentered,
        'is-round': this.isRound,
        'is-flat': this.isFlat,
        'is-outlined': this.isOutlined,
        'is-icon-only': this.isIconOnly,
        'is-loading': this.loading,
        'is-focus': this.isFocus,
        'is-hover': this.isHover,
        'is-active': this.isActive,
        'is-skip-link': this.skipLink,
        'has-expand': !!this.$slots.expand,
        ['is-size--' + this.size]: !!this.size,
        ['is-tooltip--' + this.tooltipPosition]: !!this.tooltipPosition,
        ['color--' + this.dynamicColor]: this.hasBackgroundColor,
        ['text--' + this.dynamicTextColor]: !!this.dynamicTextColor,
      }
    },
    listeners () {
      return {
        'mouseover': (e) => {
          this.isHover = true
          this.onHover(e)
        },
        'mouseleave': (e) => {
          this.isHover = false
          this.onLeave(e)
        },
        'mousedown': (e) => {
          this.isActive = true
          this.onActive(e)
        },
        'mouseup': (e) => {
          this.isActive = false
          this.onClick(e)
        },
        'touchstart': (e) => {
          e.stopPropagation()
          this.isActive = true
          this.hasTouchMoved = false
          this.onActive(e)
        },
        'touchmove': () => {
          this.isActive = false
          this.hasTouchMoved = true
        },
        'touchend': (e) => {
          if (this.hasTouchMoved) return
          e.stopPropagation()
          e.preventDefault()
          this.isActive = false
          this.onTap(e)
        },
        'keydown': (e) => {
          if (e.code == 'Enter' || e.code == 'Space') {
            e.stopPropagation()
            this.isActive = true
            this.onActive(e)
            this.onClick(e)
          }
        },
        'keyup': (e) => {
          if (e.code == 'Enter' || e.code == 'Space') {
            e.stopPropagation()
            this.isActive = false
          }
        },
        'focus': (e) => {
          this.isFocus = true
          this.onFocus(e)
        },
        'blur': (e) => {
          this.isFocus = false
          this.isActive = false
          this.onBlur(e)
        },
      }
    },
    isWide () {
      return this.wide
    },
    isCentered () {
      return this.centered
    },
    isRound () {
      return this.round
    },
    isFlat () {
      return this.flat
    },
    isOutlined () {
      return this.outlined
    },
    isIconOnly () {
      const elementsInSlot = this.$slots.default?.filter(vnode => {
        if (vnode.text && vnode.text.trim() == '') return false
        if (vnode.isComment) return false
        return true
      })
      return (
        elementsInSlot
        && elementsInSlot.length == 1
        && elementsInSlot[0].componentOptions
        && (elementsInSlot[0].componentOptions.tag == 'eo-icon' || elementsInSlot[0].componentOptions.tag == 'eo-spinner')
      )
    },
    href () {
      if (this.dynamicTag === 'a' || this.dynamicTag === 'nuxt-link' || this.dynamicTag === 'router-link') {
        return this.to || null
      } else {
        return null
      }
    },
    hasBackgroundColor () {
      if (this.dynamicColor == null) return false
      if (this.isFlat) return false
      return true
    },
    dynamicColor () {
      /**
       * Returns the background color of the button based on its state
       */
      if (this.color == null) return null
      const isBrighteningPossible = this.$color.step(this.color, -2)
      if (this.isActive) return this.$color.step(this.color, isBrighteningPossible ? -2 : 2)
      if (this.isFocus) return this.$color.step(this.color, isBrighteningPossible ? -1 : 1)
      if (this.isHover) return this.$color.step(this.color, isBrighteningPossible ? -1 : 1)
      return this.color
    },
    dynamicTextColor () {
      /**
       * Returns the text color of the button based on its state
       */
      const textColor = this.textColor || this.$color.getForeground(this.color)
      if (!textColor) return null

      if (this.isFlat) {
        const isBrighteningPossible = this.$color.step(textColor, -1)
        if (this.isActive || this.isFocus || this.isHover) {
          return this.$color.step(textColor, isBrighteningPossible ? -1 : 1)
        }
      }
      return textColor
    },
    dynamicTag () {
      if (this.to) {
        if (
          this.to.startsWith('https://') ||
          this.to.startsWith('http://') ||
          this.to.startsWith('tel:') ||
          this.to.startsWith('mailto:') ||
          this.to.startsWith('whatsapp:') ||
          this.to.startsWith('#') ||
          this?.target?.toLowerCase() === '_parent' ||
          this?.target?.toLowerCase() === '_top'
        ) {
          return 'a'
        }
        if (this.$root.$options.components.NuxtLink) {
          return 'nuxt-link'
        } else if (this.$root.$options.components.RouterLink) {
          return 'router-link'
        } else {
          return 'a'
        }
      }
      return 'button'
    },
  },
  methods: {
    onTap (e) {
      /**
       * Manual tap event for stable iOS interaction with the card
       * Because :active state doesnt show when card has no touch-listeners
       */
      if (this.hasTouchMoved) return
      e.preventDefault()
      e.stopPropagation()
      this.onClick(e)

      // Manually fire a click event on the hyperlink
      if (this.to || this.type === 'submit') {
        const anchorTag = this.$refs?.hyperlink.$el || this.$refs?.hyperlink
        anchorTag.click()
      }
    },
    onClick: throttle(function ($event) {
      /**
       * Emitted when the user clicks the button
       * @event click
       */
      $event.stopPropagation()
      this.$emit('click', $event)
    }, 300),
    onHover: throttle(function ($event) {
      /**
       * Emitted when the user hovers the button
       * @event hover
       */
      this.$emit('hover', $event)
    }, 300),
    onLeave: throttle(function ($event) {
      /**
       * Emitted when the user's cursor leaves the button
       * @event leave
       */
      this.$emit('leave', $event)
    }, 300),
    onActive: throttle(function ($event) {
      /**
       * Emitted when the user focuses on the button
       * @event active
       */
      this.$emit('active', $event)
    }, 300),
    onFocus: throttle(function ($event) {
      /**
       * Emitted when the user focuses on the button
       * @event focus
       */
      this.$emit('focus', $event)
    }, 300),
    onBlur: throttle(function ($event) {
      /**
       * Emitted when the user loses focus on the button
       * @event blur
       */
      this.$emit('blur', $event)
    }, 300),
  },
  render: function (createElement) {
    return createElement(
      this.dynamicTag,
      {
        class: this.classes,
        props: {
          to: this.to,
          prefetch: this.dynamicTag === 'nuxt-link' ? this.prefetch : null,
        },
        attrs: {
          href: this.href,
          type: this.type || (this.dynamicTag === 'button' ? 'button' : null),
          target: this.target,
          'aria-label': this.label,
          'aria-describedby': this.tooltip,
          disabled: this.disabled,
          role: this.dynamicTag === 'button' ? null : 'button',
        },
        ref: 'hyperlink',
        on: (this.dynamicTag === 'nuxt-link' || this.dynamicTag === 'router-link') ? null : this.listeners,
        nativeOn: (this.dynamicTag === 'nuxt-link' || this.dynamicTag === 'router-link') ? this.listeners : null,
      },
      [
        this.$slots.default ? createElement(
          'span',
          {
            class: 'eo-button__content',
          },
          [
            /**
             * @slot Default content
             */
            this.$slots.default,
            createElement(
              'eo-transition-accordion',
              {
                props: {
                  direction: 'x',
                  duration: 150,
                },
              },
              [
                createElement(
                  'span',
                  {
                    directives: [{
                      name: 'show',
                      value: this.isHover && !this.loading,
                    }],
                    class: 'text--left',
                    slot: 'expand',
                  },
                  /**
                   * @slot Appears on hover with cursor
                   */
                  this.$slots.expand,
                ),
              ],
            ),
          ],
        ) : null,
        createElement(
          'span',
          {
            directives: [{
              name: 'show',
              value: this.loading,
            }],
            class: 'eo-button__loader',
            slot: 'loader',
          },
          [
            this.loading ?
              (this.$slots.loader ?
              /**
               * @slot Shows when button is loading (by default an EoSpinner)
               */
              this.$slots.loader :
              createElement(
                'eo-spinner',
                {
                  props: {
                    href: 'spinner',
                    size: 'sm',
                    color: this.dynamicTextColor,
                    colorSecondary: 'secondary',
                    compact: true,
                  },
                },
              )) : null,
          ],
        ),
        this.tooltip ? createElement(
          'span',
          {
            class: 'eo-button__tooltip',
            id: this._uid + '-tooltip',
            attrs: {
              'data-text': this.tooltip,
              'role': 'tooltip',
            },
          },
        ): null,
      ],
    )
  },
}
